/**
 * Frontend helpers for module access based on effective permissions.
 *
 * permissionsMap is expected to be of the form:
 *   { [module: string]: 'view' | 'edit' | 'full_access' }
 * mirroring the backend PermissionLevel enum.
 */

const LEVEL_ORDER = {
  view: 1,
  edit: 2,
  full_access: 3,
};

export function hasModuleAccess(permissionsMap, module) {
  // If permissions haven't been loaded yet, optimistically allow.
  // Backend still enforces access via API permissions.
  if (!permissionsMap) return true;
  return Boolean(permissionsMap[module]);
}

export function hasAtLeastPermission(permissionsMap, module, requiredLevel) {
  if (!permissionsMap) return false;
  const current = permissionsMap[module];
  if (!current) return false;
  return (LEVEL_ORDER[current] || 0) >= (LEVEL_ORDER[requiredLevel] || 0);
}

export function getAccessibleModules(permissionsMap) {
  if (!permissionsMap) return [];
  return Object.keys(permissionsMap).filter((module) => Boolean(permissionsMap[module]));
}

// Default role-based permissions used by the frontend when
// configuring UI visibility and edit capabilities. This mirrors
// the backend MODULE_ACCESS_MATRIX but encodes a permission level
// for each enabled module.
export function getDefaultPermissionsForRole(userType) {
  switch (userType) {
    case 'gods_eye':
      return {
        dashboard: 'full_access',
        companies: 'full_access',
        users: 'full_access',
        devices: 'full_access',
        depots: 'full_access',
        device_assignments: 'full_access',
        container_assignment: 'full_access',
        notifications: 'full_access',
        reports: 'full_access',
        audit_logs: 'full_access',
      };
    case 'manager':
      return {
        dashboard: 'full_access',
        companies: 'full_access',
        users: 'full_access',
        devices: 'full_access',
        depots: 'full_access',
        device_assignments: 'full_access',
        container_assignment: 'full_access',
        notifications: 'full_access',
        reports: 'full_access',
        audit_logs: 'full_access',
      };
    case 'cliente':
      return {
        dashboard: 'full_access',
        users: 'full_access',
        devices: 'full_access',
        depots: 'full_access',
        notifications: 'full_access',
        device_assignments: 'full_access',
        container_assignment: 'full_access',
        reports: 'full_access',
      };
    case 'operador':
      return {
        dashboard: 'full_access',
        devices: 'full_access',
        depots: 'full_access',
        container_assignment: 'full_access',
        device_assignments: 'full_access',
        reports: 'full_access',
      };
    case 'verificador':
      return {
        dashboard: 'full_access',
        devices: 'full_access',
        container_assignment: 'full_access',
        notifications: 'full_access',
        device_assignments: 'full_access',
      };
    case 'distribuidor':
      return {
        dashboard: 'full_access',
        device_assignments: 'full_access',
        reports: 'full_access',
      };
    default:
      return {};
  }
}

/**
 * Module metadata for navigation
 */
export const MODULES = {
  dashboard: {
    name: 'Dashboard',
    path: '/',
    label: 'Dashboard',
  },
  companies: {
    name: 'Companies',
    path: '/companies',
    label: 'Companies',
  },
  users: {
    name: 'Users',
    path: '/users',
    label: 'Users',
  },
  devices: {
    name: 'Seals',
    path: '/devices',
    label: 'Seals',
  },
  depots: {
    name: 'Depots',
    path: '/depots',
    label: 'Depots',
  },
  device_assignments: {
    name: 'Box Assignments',
    path: '/box-assignments',
    label: 'Box Assignments',
  },
  container_assignment: {
    name: 'Container Assignment',
    path: '/container-assignment',
    label: 'Assign Containers',
  },
  notifications: {
    name: 'Notifications',
    path: '/notifications',
    label: 'Notifications',
  },
  reports: {
    name: 'Reports',
    path: '/reports',
    label: 'Reports',
  },
  audit_logs: {
    name: 'Audit Logs',
    path: '/audit-logs',
    label: 'Audit Logs',
  },
};
